import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Users, Building2, Target, CalendarClock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, PageBody } from "./_PageShell";
import { fmtCompact } from "@/lib/format";

const tiles = [
  { key: "contacts", label: "Contacts", to: "/app/crm/contacts", Icon: Users },
  { key: "companies", label: "Companies", to: "/app/crm/companies", Icon: Building2 },
  { key: "deals", label: "Open Deals", to: "/app/crm/deals", Icon: Target },
  { key: "pipeline_value", label: "Pipeline Value", to: "/app/crm/deals", Icon: CalendarClock, money: true },
];

export default function Dashboard() {
  const { user, workspace } = useAuth();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/crm/dashboard")
      .then(({ data }) => setStats(data))
      .catch((e) => setError(apiError(e)));
  }, []);

  const firstName = (user?.name || user?.email || "there").split(" ")[0];

  return (
    <>
      <PageHeader
        eyebrow="Dashboard"
        title={
          <>
            Welcome back, <span className="italic">{firstName}.</span>
          </>
        }
        description={
          workspace
            ? `Workspace · ${workspace.name}`
            : "Loading your workspace…"
        }
      />
      <PageBody>
        {error && (
          <div className="mb-6 text-sm text-[#B03A2E]">{error}</div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {tiles.map((t) => {
            const Icon = t.Icon;
            const raw = stats ? stats[t.key] ?? 0 : null;
            const value = raw === null ? "—" : t.money ? fmtCompact(raw, "INR") : raw;
            return (
              <Link
                key={t.key}
                to={t.to}
                data-testid={`stat-${t.key}`}
                className="group p-6 rounded-2xl bg-[#FDFCFB] border border-[#E8E6E1] hover:border-[#1A1A1A]/30 hover:-translate-y-0.5 hover:shadow-[0_22px_40px_-20px_rgba(0,0,0,0.18)] transition-all flex flex-col min-h-[160px]"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-10 h-10 rounded-xl bg-[#F5F4F0] flex items-center justify-center group-hover:bg-[#1A1A1A] group-hover:text-[#FDFCFB] transition-colors">
                    <Icon className="w-4.5 h-4.5" strokeWidth={1.6} />
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-[#1A1A1A]/30 transition-all group-hover:text-[#1A1A1A] group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
                <div className="font-serif-display text-4xl tracking-tight text-[#1A1A1A] leading-none">
                  {value}
                </div>
                <div className="mt-3 caption !text-[11px]">{t.label}</div>
              </Link>
            );
          })}
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 p-8 rounded-2xl bg-[#1A1A1A] text-[#FDFCFB]">
            <div className="caption !text-[#FDFCFB]/50 mb-4">Get started</div>
            <h2 className="font-serif-display text-3xl tracking-tight leading-snug max-w-md">
              Start by adding your first <span className="italic">contact.</span>
            </h2>
            <p className="mt-4 text-sm text-[#FDFCFB]/65 max-w-lg leading-relaxed">
              Bring in leads from your last spreadsheet, your inbox or a CSV. Once
              there's data in the system, you can build pipelines and start
              tracking activity.
            </p>
            <Link
              to="/app/crm/contacts"
              data-testid="dashboard-cta-contacts"
              className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FDFCFB] text-[#1A1A1A] text-sm hover:bg-[#FDFCFB]/90 transition-all"
            >
              Go to contacts
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="p-6 rounded-2xl bg-[#F5F4F0]">
            <div className="caption mb-4">Coming next</div>
            <ul className="space-y-3 text-sm text-[#1A1A1A]">
              <li className="flex items-start gap-2">
                <span className="font-mono-tech text-[10px] tracking-[0.18em] text-[#6B6A68] mt-1">
                  01
                </span>
                Drag-and-drop Kanban deal board
              </li>
              <li className="flex items-start gap-2">
                <span className="font-mono-tech text-[10px] tracking-[0.18em] text-[#6B6A68] mt-1">
                  02
                </span>
                Tasks, notes and calendar
              </li>
              <li className="flex items-start gap-2">
                <span className="font-mono-tech text-[10px] tracking-[0.18em] text-[#6B6A68] mt-1">
                  03
                </span>
                CSV import & team invites
              </li>
            </ul>
          </div>
        </div>
      </PageBody>
    </>
  );
}
